import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  ECustomFieldType,
  ICustomField,
  ICustomFieldValue,
} from '../../../custom-field/interfaces';
import { SnackBarConfig } from '../../../shared/configs';
import { CustomFieldParseValuePipe } from '../../../shared/pipes/custom-field-parse-value.pipe';
import { OrderApiService } from '../../../shared/services';

interface ICustomFieldCellEditorParams extends ICellEditorParams {
  customFieldLabel :string;
  customFields :ICustomField[];
}

@Component({
  selector: 'lum-sales-order-custom-field-cell-editor',
  templateUrl: './sales-order-custom-field-cell-editor.component.html',
  styleUrls: ['./sales-order-custom-field-cell-editor.component.scss'],
})
export class SalesOrderCustomFieldCellEditorComponent
  implements ICellEditorAngularComp, OnDestroy
{
  value :string;

  customFieldType = ECustomFieldType;
  customFieldValue :ICustomFieldValue;
  customField :ICustomField | null;
  customFields :ICustomField[] = [];
  customFieldValueIndex :number = -1;

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  private cellEditorParams :ICustomFieldCellEditorParams;
  private originalValue :string;

  private onDestroy$ = new Subject<void>();

  constructor(
    private customFieldParserPipe :CustomFieldParseValuePipe,
    private orderApiService :OrderApiService,
    private snackbar :MatSnackBar,
  ) {}

  agInit(params :ICustomFieldCellEditorParams) :void {
    this.cellEditorParams = params;

    this.initCustomFields();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  getValue() :string {
    return this.value;
  }

  saveCustomField(event ?:Event) :void {
    if (event) {
      event.preventDefault();
    }

    // if custom field is null, throw error and stop editing
    if (!this.customField) {
      this.snackbar.open('Cannot update custom field.', 'Error', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.onCancelEditing();

      return;
    }

    // prevent service call if no changes in value
    if (this.value === this.originalValue) {
      this.onCancelEditing();

      return;
    }

    this._loading.next(true);
    this.orderApiService
      .updateCustomField(this.cellEditorParams.data.id, {
        customFieldId: this.customField.id,
        value: this.value,
      })
      .pipe(
        finalize(() => {
          this._loading.next(false);
          this.onCancelEditing();
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (response :ICustomFieldValue) => {
          if (response?.id) {
            // updated or inserted
            if (this.customFieldValueIndex >= 0) {
              this.cellEditorParams.data.customFieldValues[
                this.customFieldValueIndex
              ] = response;
            } else {
              this.cellEditorParams.data.customFieldValues.push(response);
            }
          } else {
            // deleted
            this.cellEditorParams.data.customFieldValues.splice(
              this.customFieldValueIndex,
              1,
            );
          }

          this.cellEditorParams.api.refreshCells({
            rowNodes: [this.cellEditorParams.node],
            force: true,
          });

          this.snackbar.open('Custom field updated.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackbar.open('Error updating custom field.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onCancelEditing() :void {
    this.cellEditorParams.stopEditing();
    this.cellEditorParams.api.stopEditing();
  }

  private initCustomFields() :void {
    const customFieldlabel = this.cellEditorParams.customFieldLabel;
    const customFields = this.cellEditorParams.customFields;

    // get index - used to determine wether customfield is updated, newly created, or deleted
    this.customFieldValueIndex =
      this.cellEditorParams?.data?.customFieldValues.findIndex(
        (cf :ICustomFieldValue) => cf.customField.label === customFieldlabel,
      );

    // actual cell custom field value
    this.customFieldValue =
      this.cellEditorParams?.data?.customFieldValues[this.customFieldValueIndex];

    // custom field data
    this.customField =
      customFields.find((cf :ICustomField) => cf.label === customFieldlabel) ||
      null;

    // get column value
    const value = this.customFieldParserPipe.transform(
      this.customFieldValue,
      false,
    );
    this.value = value !== null ? String(value) : '';
    this.originalValue = this.value;
  }
}
